import React, { useState, useEffect } from "react";
import LaddaButton, { ZOOM_OUT, XS, SLIDE_LEFT } from "react-ladda";
import { Table, Button } from "reactstrap";
import { LoadingMessage } from "../../Controls";
import { DateField } from "../../../../utils";
import { addMonth } from "./../../../Modal/ActivityV2/helper";
import { FormattedDate } from "react-intl";
import { FormattedNumber } from "../../../FormattedNumber/FormattedNumber";
import ModalQuote, { parseDate } from "./../../Modals/ModalQuote";
import style from "./styles.module.scss";
import { useEntities } from "../../Wizzard";
import { WizzMode, Mode } from "../../types";

const Quotes = ({ mode, assetId, currencies, fields, services, wizzToggle, wizzOnChange, decimalScale = null }) => {
  const [quotes, setQuotes] = useState(false);
  const [readonly, setReadonly] = useState(false);
  const [modalQuote, setModalQuote] = useState({ open: false });
  const [deleting, setDeleting] = useEntities({});

  const [filter, setFilter] = useState({
    FromDate: addMonth(new Date(), -1),
    ToDate: new Date(),
  });

  useEffect(() => {
    setQuotes(false);
    services.quotes({ assetId, filter }).then(({ result, quotes }) => {
      setQuotes(quotes);
    });
  }, [filter]);

  useEffect(() => {
    if (mode) {
      setReadonly(mode == WizzMode.View);
    }
  }, [mode]);

  const toggle = (props) => {
    setModalQuote({ open: !modalQuote.open, ...props });
  };

  // handle grid actions
  const handleAction = (action, quoteDate) => {
    if (action == Mode.Insert) {
      toggle({
        assetId,
        action,
        onConfirm: (e) => handleSave(action, e),
      });
    } else if (action == Mode.Update) {
      toggle({
        assetId,
        action,
        quote: quotes.find((p) => p.AssetQuoteDate == quoteDate),
        onConfirm: (e) => handleSave(action, e),
      });
    } else if (action == Mode.Delete) {
      wizzToggle("confirm", {
        title: "Delete quote",
        message: "Are you sure you want to delete quote?",
        onConfirm: () => handleDelete(quoteDate),
      });
    }
  };

  // callback after modal confirm
  const handleSave = (action, quote) => {
    return services
      .updateQuote({
        assetId,
        action,
        quote,
      })
      .then((res) => res.result)
      .then((result) => {
        if (result.Code == 200) {
          setQuotes([quote].concat(quotes.filter((e) => e.AssetQuoteDate != quote.AssetQuoteDate)));
        } else {
          wizzToggle("message", {
            title: "Error",
            message: result.Description,
          });
        }

        return result.Code;
      });
  };

  // callback after confirm delete
  const handleDelete = (id) => {
    setDeleting({ ...deleting, [id]: true });

    services
      .updateQuote({
        action: "DLT",
        assetId,
        quote: quotes.find((e) => e.AssetQuoteDate == id),
      })
      .then((res) => res.result)
      .then((result) => {
        setDeleting({
          ...deleting,
          [id]: false,
        });
        if (result.Code == 200) {
          setQuotes(quotes.filter((e) => e.AssetQuoteDate != id));
        } else {
          wizzToggle("message", {
            title: "Error",
            message: result.Description,
          });
        }
      });
  };

  return (
    <div>
      <div className={style.filter}>
        <div className={style.field}>
          <div className={style.label}>From Date</div>
          <div className={style.formDate}>
            <DateField
              disabled={false}
              label=""
              name="FromDate"
              onChange={(name, value) => setFilter({ ...filter, FromDate: value })}
              value={filter.FromDate || new Date().toLocaleDateString("en")}
            />
          </div>
        </div>
        <div className={style.field}>
          <div className={style.label}>To Date</div>
          <div className={style.formDate}>
            <DateField
              disabled={false}
              label=""
              name="ToDate"
              onChange={(name, value) => setFilter({ ...filter, ToDate: value })}
              value={filter.ToDate || new Date().toLocaleDateString("en")}
            />
          </div>
        </div>
      </div>
      {!readonly && (
        <Button color="primary" className="mb-3" onClick={() => handleAction(Mode.Insert)}>
          <i className="fa fa-plus mr-1" aria-hidden="true"></i>
          Add quote
        </Button>
      )}
      <Table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Currency</th>
            <th className="text-right">Price</th>
            <th className="text-right">Accounting Price</th>
            <th className="text-right">Adjusted Price</th>
            <th className="text-right">Open Price</th>
            <th className="text-right">Close Price</th>
            <th className="text-right">Quotes Dirty</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {quotes ? (
            <React.Fragment>
              {quotes.map((q) => (
                <tr key={`q-${q.AssetQuoteDate}`}>
                  <td>
                    <FormattedDate value={`${q.AssetQuoteDate}T00:00`} />
                  </td>
                  <td>{q.QuoteCurrencyISO}</td>
                  <td className="text-right">
                    <FormattedNumber
                      value={q.AssetQuotePrice}
                      decimalScale={decimalScale}
                      dataTypeDecimalName="price"
                    />
                  </td>
                  <td className="text-right">
                    <FormattedNumber
                      value={q.AssetQuoteAccountingPrice}
                      decimalScale={decimalScale}
                      dataTypeDecimalName="price"
                    />
                  </td>
                  <td className="text-right">
                    <FormattedNumber
                      value={q.AssetQuoteAdjustedPrice}
                      decimalScale={decimalScale}
                      dataTypeDecimalName="price"
                    />
                  </td>
                  <td className="text-right">
                    <FormattedNumber
                      value={q.AssetQuoteOpenPrice}
                      decimalScale={decimalScale}
                      dataTypeDecimalName="price"
                    />
                  </td>
                  <td className="text-right">
                    <FormattedNumber
                      value={q.AssetQuoteClosePrice}
                      decimalScale={decimalScale}
                      dataTypeDecimalName="price"
                    />
                  </td>
                  <td className="text-right">
                    {q.AssetQuotePriceIsDirty? 'YES':'NO'}
                  </td>
                  <td>
                    {!readonly && (
                      <Actions
                        id={q.AssetQuoteDate}
                        action={handleAction}
                        deleting={!!deleting[q.AssetQuoteDate]}
                      />
                    )}
                  </td>
                </tr>
              ))}
              {quotes.length == 0 && <EmptyOrLoading />}
            </React.Fragment>
          ) : (
            <EmptyOrLoading loading />
          )}
        </tbody>
      </Table>
      {modalQuote.open && (
        <ModalQuote
          {...modalQuote}
          currencies={currencies}
          quotesDirty={fields.AssetPricesDirty}
          assetId={assetId}
          toggle={toggle}
          decimalScale={decimalScale}
        />
      )}
    </div>
  );
};

const EmptyOrLoading = ({ loading }) => (
  <tr>
    <td colSpan="7">
      {loading ? (
        <LoadingMessage text="Loading asset quotes" />
      ) : (
        <span className="text-center d-block h5 m-3 mt-4" style={{ textAlign: "center" }}>
          No asset quotes
        </span>
      )}
    </td>
  </tr>
);

const Actions = ({ action, id, deleting }) => (
  <div className="d-flex justify-content-end">
    <Button color="primary" size="sm" className="mr-2" onClick={() => action(Mode.Update, id)}>
      <i className="fa fa-pencil-square-o" aria-hidden="true"></i>
    </Button>

    <LaddaButton
      className="btn btn-ladda btn-danger btn-sm"
      loading={deleting}
      onClick={() => action(Mode.Delete, id)}
      data-style={ZOOM_OUT}
      data-size={XS}
    >
      <i className="fa fa-trash-o" aria-hidden="true"></i>
    </LaddaButton>
  </div>
);

export default Quotes;
